import { useState } from "react";
import { PageHeader } from "../../page-header";
import { usePagamentoLote } from "../../../hooks/usePagamentoLote";
import { formatValueToMonetary } from "../../../utils/formatter";
import { Link } from "react-router-dom";
import { TbLayoutList, TbTrash } from "react-icons/tb";
import { apiV1 } from "../../../lib/axios";
import { toast } from "react-toastify";
import "../PagamentoLote.css";
import "./PagamentoLoteLotes.css";
import moment from "moment";

const BATCH_STATUS_LABELS = {
  RASCUNHO: "Rascunho",
  AGUARDANDO_APROVACAO: "Aguardando aprovação",
  EM_PROCESSAMENTO: "Em processamento",
  PAGO: "Pago",
  PARCIALMENTE_PAGO: "Parcialmente pago",
  COM_ALERTA: "Com alerta",
  FALHOU: "Falhou",
  REJEITADO: "Rejeitado",
};

function batchStatusBadgeClass(status) {
  const map = {
    RASCUNHO: "pendente",
    AGUARDANDO_APROVACAO: "pendente",
    EM_PROCESSAMENTO: "pendente",
    PAGO: "aprovado",
    PARCIALMENTE_PAGO: "alerta",
    COM_ALERTA: "alerta",
    FALHOU: "alerta",
    REJEITADO: "alerta",
  };
  return map[status] || "pendente";
}

function canDelete(batch) {
  return (
    batch.status === "AGUARDANDO_APROVACAO" ||
    batch.status === "RASCUNHO" ||
    batch.status === "FALHOU" ||
    batch.status === "REJEITADO"
  );
}

export default function PagamentoLoteLotes() {
  const { batches, stats, submitBatch, deleteBatch } = usePagamentoLote();
  const [confirmBatch, setConfirmBatch] = useState(null);
  const [checking, setChecking] = useState(false);

  // Statuses where payments could have been executed — needs verification
  const NEEDS_PAYMENT_CHECK = ["FALHOU", "REJEITADO", "PARCIALMENTE_PAGO", "COM_ALERTA"];
  const SUCCESS_STATUSES = ["PAGO", "APROVADO", "PROCESSADO"];

  async function handleDeleteClick(batch) {
    if (!NEEDS_PAYMENT_CHECK.includes(batch.status)) {
      // RASCUNHO / AGUARDANDO_APROVACAO: nenhum pagamento foi executado
      setConfirmBatch(batch);
      return;
    }
    try {
      setChecking(true);
      const response = await apiV1.get(`/batch-payments/payments?batchId=${batch.id}`);
      const payments = response.data.data || [];
      const hasPaid = payments.some((p) =>
        SUCCESS_STATUSES.includes((p.status || "").toUpperCase())
      );
      if (hasPaid) {
        toast.error("Este lote possui pagamentos realizados com sucesso e não pode ser excluído.");
        return;
      }
      setConfirmBatch(batch);
    } catch {
      toast.error("Não foi possível verificar os pagamentos do lote. Tente novamente.");
    } finally {
      setChecking(false);
    }
  }

  async function handleDeleteConfirm() {
    if (!confirmBatch) return;
    await deleteBatch(confirmBatch.id);
    setConfirmBatch(null);
  }

  return (
    <div className="paylote-page">
      <div className="paylote-inner col-md-10 m-auto mt-4">
        <div className="mb-3">
          <PageHeader
            title="Lotes"
            pages={[
              { name: "Pagamento em Lote", link: "/pagamento-lote/dashboard" },
              { name: "Lotes" },
            ]}
          />
        </div>

        {/* Summary metrics */}
        <section className="paylote-metrics paylote-lotes-metrics">
          <div className="paylote-metric-card">
            <div className="paylote-metric-icon blue"><TbLayoutList size={20} /></div>
            <div>
              <div className="paylote-metric-label">Total de lotes</div>
              <div className="paylote-metric-value">{batches.length}</div>
              <div className="paylote-metric-small">cadastrados</div>
            </div>
          </div>
          <div className="paylote-metric-card">
            <div className="paylote-metric-icon orange"><TbLayoutList size={20} /></div>
            <div>
              <div className="paylote-metric-label">Aguardando aprovação</div>
              <div className="paylote-metric-value">
                {batches.filter(b => b.status === "AGUARDANDO_APROVACAO").length}
              </div>
              <div className="paylote-metric-small">lotes abertos</div>
            </div>
          </div>
          <div className="paylote-metric-card">
            <div className="paylote-metric-icon green"><TbLayoutList size={20} /></div>
            <div>
              <div className="paylote-metric-label">Total aprovado</div>
              <div className="paylote-metric-value">{formatValueToMonetary(stats.approved.amount)}</div>
              <div className="paylote-metric-small">{stats.approved.count} contas</div>
            </div>
          </div>
        </section>

        {/* Batch table */}
        <section className="paylote-table-card">
          <div className="paylote-table-toolbar">
            <strong className="paylote-lotes-section-title">
              Todos os lotes
            </strong>
            <div className="paylote-search-area">
              <Link to="/pagamento-lote/novo" className="paylote-btn-primary" style={{ textDecoration: "none" }}>
                + Novo lote
              </Link>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>ID do lote</th>
                <th>Criado por</th>
                <th>Data de criação</th>
                <th>Contas</th>
                <th>Valor total</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => (
                <tr key={batch.id}>
                  <td><strong>#{batch.id}</strong></td>
                  <td>{batch.createdByName}</td>
                  <td className="paylote-lotes-date-cell">{moment(batch.createdAt, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY")}</td>
                  <td>{batch.totalBills}</td>
                  <td><strong>{formatValueToMonetary(batch.totalAmount)}</strong></td>
                  <td>
                    <span className={`paylote-badge ${batchStatusBadgeClass(batch.status)}`}>
                      {BATCH_STATUS_LABELS[batch.status] || batch.status}
                    </span>
                  </td>
                  <td className="paylote-lotes-actions-cell">
                    <div>
                      <span>
                        {batch.status === "RASCUNHO" && (
                          <button
                            className="paylote-lotes-approve-link"
                            onClick={() => submitBatch(batch.id)}
                            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                          >
                            Submeter →
                          </button>
                        )}
                        {batch.status === "AGUARDANDO_APROVACAO" && (
                          <Link
                            to={`/pagamento-lote/aprovacao?batchId=${batch.id}`}
                            className="paylote-lotes-approve-link"
                          >
                            Aprovar →
                          </Link>
                        )}
                      </span>
                      <span>
                        {canDelete(batch) && (
                          <button
                            className="paylote-lotes-delete-btn"
                            onClick={() => handleDeleteClick(batch)}
                            disabled={checking}
                            title="Excluir lote"
                          >
                            <TbTrash size={16} />
                          </button>
                        )}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      {/* Modal de confirmação de exclusão */}
      {confirmBatch && (
        <div className="paylote-modal-overlay" onClick={() => setConfirmBatch(null)}>
          <div className="paylote-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="paylote-modal-title">Excluir lote</h3>
            <p className="paylote-modal-body">
              Tem certeza que deseja excluir o lote{" "}
              <strong>#{confirmBatch.id}</strong>?<br />
              Esta ação não pode ser desfeita.
            </p>
            <div className="paylote-modal-footer">
              <button className="paylote-btn-secondary" onClick={() => setConfirmBatch(null)}>
                Cancelar
              </button>
              <button className="paylote-btn-danger" onClick={handleDeleteConfirm}>
                Confirmar exclusão
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
